import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { House, Users, Palette, Sparkles, LayoutGrid, TrendingUp, Coins, LogOut, CreditCard, Sun, Moon } from "lucide-react";
import { useTheme } from "next-themes";
import { useAuth } from "@/context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { api, fileUrl } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { isMinistry } from "@/lib/vocab";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/home", label: "Home", icon: House, id: "home" },
  { to: "/brand", label: "Brand", icon: Palette, id: "brand" },
  { to: "/create", label: "Create", icon: Sparkles, id: "create" },
  { to: "/library", label: "Library", icon: LayoutGrid, id: "library" },
  { to: "/crm", label: "CRM", icon: Users, id: "crm" },
  { to: "/grow", label: "Grow", icon: TrendingUp, id: "grow" },
];

export function ThemeToggle({ className }) {
  const { theme, setTheme } = useTheme();
  const dark = theme !== "light";
  return (
    <button type="button" aria-label="Toggle theme" data-testid="theme-toggle" onClick={() => setTheme(dark ? "light" : "dark")} className={cn("btn-quiet h-9 w-9 px-0", className)}>
      {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

export function LogoMark({ className = "h-7 w-7" }) {
  return <img src="/legacystack-mark.png" alt="LegacyStack AI" className={className} />;
}

export function CreditsPill({ credits }) {
  return (
    <NavLink to="/billing" data-testid="credits-pill" className="inline-flex items-center gap-2 rounded-full border border-border px-3 h-9 text-xs font-mono-ls hover:border-gold/60 transition-colors">
      <Coins className="h-3.5 w-3.5 text-gold" />
      <span data-testid="credit-balance">{credits ?? "—"}</span>
      <span className="text-muted-foreground">credits</span>
    </NavLink>
  );
}

export function Shell() {
  const { user, org, logout } = useAuth();
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ["home"], queryFn: async () => (await api.get("/home")).data, refetchInterval: 15000 });
  const credits = data?.credits;
  const { resolvedTheme } = useTheme();
  const orgLogo = data?.brand?.tokens?.logo;
  const brandLogo = orgLogo?.uploaded ? (resolvedTheme === "light" ? orgLogo.light_url : orgLogo.dark_url) : null;

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-60 flex-col border-r border-border surface z-30" data-testid="side-rail">
        <Link to="/home" aria-label="LegacyStack AI home" className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gold h-16 flex items-center gap-3 px-6 border-b border-border">
          <LogoMark />
          <div className="leading-tight">
            <div className="font-display text-base">LegacyStack AI</div>
            <div className="eyebrow-label !text-[9px]">AI OS</div>
          </div>
        </Link>
        <nav className="flex-1 px-3 py-6 space-y-1" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.id}`} className={({ isActive }) => cn("flex items-center gap-3 rounded-xl px-3 h-11 text-sm transition-colors", isActive ? "bg-muted text-foreground border-l-2 border-gold" : "text-muted-foreground hover:text-foreground hover:bg-muted/60")}>
              <n.icon className="h-4 w-4" />
              {isMinistry(org) && n.id === "grow" ? "Mission" : isMinistry(org) && n.id === "crm" ? "People" : n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-6 py-5 border-t border-border">
          <div className="eyebrow-label mb-1">{org?.org_type === "business" ? "Business" : org?.org_type === "nonprofit" ? "Nonprofit" : "Ministry"}</div>
          <div className="text-sm font-medium truncate" data-testid="rail-org-name">{org?.name}</div>
        </div>
      </aside>

      <div className="md:pl-60">
        <header className="sticky top-0 z-20 h-16 flex items-center justify-between px-4 sm:px-6 border-b border-border bg-background/80 glass">
          <Link to="/home" aria-label="LegacyStack AI home" className="flex items-center gap-3 md:hidden rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold">
            <LogoMark className="h-6 w-6" />
            <span className="font-display">LegacyStack AI</span>
          </Link>
          <div className="hidden md:flex items-center gap-3">
            {brandLogo && <img src={fileUrl(brandLogo)} alt={`${org?.name} logo`} className="h-7 w-auto max-w-[80px] object-contain" data-testid="header-org-logo" />}
            <span className="eyebrow-label">{org?.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <CreditsPill credits={credits} />
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" data-testid="avatar-menu" className="h-9 w-9 rounded-full overflow-hidden border border-border bg-muted flex items-center justify-center text-xs font-semibold">
                  {user?.avatar_url ? <img src={user.avatar_url} alt="" className="h-full w-full object-cover" /> : (user?.name || "?").slice(0, 1).toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-2">
                  <div className="text-sm font-medium truncate">{user?.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/billing")} data-testid="menu-billing"><CreditCard className="h-4 w-4 mr-2" />Account & billing</DropdownMenuItem>
                <DropdownMenuItem onClick={onLogout} data-testid="menu-logout"><LogOut className="h-4 w-4 mr-2" />Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="pb-24 md:pb-10">
          <Outlet />
        </main>
      </div>

      <nav className="md:hidden fixed bottom-0 inset-x-0 h-16 border-t border-border bg-background/90 glass flex items-stretch z-30" aria-label="Primary mobile" data-testid="bottom-tabs">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} data-testid={`tab-${n.id}`} className={({ isActive }) => cn("flex-1 flex flex-col items-center justify-center gap-1 text-[10px] uppercase tracking-wider", isActive ? "text-gold" : "text-muted-foreground")}>
            <n.icon className="h-5 w-5" />
            {isMinistry(org) && n.id === "grow" ? "Mission" : isMinistry(org) && n.id === "crm" ? "People" : n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
